import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SecureStorage, STORAGE_KEYS } from '@/shared/utils/secure-storage';
import { authApi } from '@/api';
import { usePermissionStore } from '@/store/permissionStore';
import type { User } from '@/shared/types';

/**
 * Action names for devtools
 */
const AUTH_ACTIONS = {
  SIGNIN: 'auth/signin',
  SIGNOUT: 'auth/signout',
  UPDATE_USER: 'auth/updateUser',
  INITIALIZE: 'auth/initializeFromStorage',
} as const;

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  updateUser: (userData: Partial<User>) => void;
  login: (user: User) => void;
  logout: () => void;
  initializeFromStorage: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isInitialized: false,

      login: user => {
        // Store user data only (tokens are in HttpOnly cookies managed by backend)
        SecureStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));

        set({ user, isAuthenticated: true }, false, AUTH_ACTIONS.SIGNIN);
      },

      logout: async () => {
        try {
          // Call backend logout API to clear cookies
          // Backend reads userId from JWT token in cookie
          await authApi.logout();
        } catch {
          // Ignore errors, proceed with local logout
          // Even if backend call fails, we still clear local state
        }

        // Clear secure storage (localStorage)
        SecureStorage.clear();

        // Clear permission store
        usePermissionStore.getState().clearPermissions();

        // Clear all sessionStorage (for any other data)
        try {
          sessionStorage.clear();
        } catch {
          // Ignore errors if sessionStorage is not available
        }

        set(
          { user: null, isAuthenticated: false },
          false,
          AUTH_ACTIONS.SIGNOUT,
        );

        // Redirect to login page
        window.location.href = '/login';
      },

      updateUser: userData => {
        const { user } = get();
        if (!user) {
          return;
        }

        const updatedUser = { ...user, ...userData };

        // Update storage
        SecureStorage.setItem(
          STORAGE_KEYS.USER_DATA,
          JSON.stringify(updatedUser),
        );

        set({ user: updatedUser }, false, AUTH_ACTIONS.UPDATE_USER);
      },

      initializeFromStorage: () => {
        const userStr = SecureStorage.getItem(STORAGE_KEYS.USER_DATA);

        if (userStr) {
          try {
            const user = JSON.parse(userStr);

            // Tokens are in HttpOnly cookies, no need to validate here
            // The backend will validate cookies on first API call
            // We'll call /auth/me to verify session is still valid
            set(
              {
                user,
                isAuthenticated: true,
                isInitialized: true,
              },
              false,
              AUTH_ACTIONS.INITIALIZE,
            );
          } catch {
            // Clear corrupted storage and allow fresh login
            SecureStorage.clear();
            set({ isInitialized: true }, false, AUTH_ACTIONS.INITIALIZE);
          }
        } else {
          // No stored auth data - user needs to login
          set({ isInitialized: true }, false, AUTH_ACTIONS.INITIALIZE);
        }
      },
    }),
    {
      name: 'auth-store',
    },
  ),
);
